import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// @ts-expect-error - react-cookies doesn't have types
import cookie from 'react-cookies';
import {
  usePrivacyContent,
  useDataPractice,
  usePrivacyActions,
  usePreferences,
  useTabConfiguration,
} from '../hooks/usePrivacyQueries';
import { useOrgSettings } from '../contexts/OrgSettingsContext';
import { TABNAMES, REQUESTTYPES } from './constant';
import {
  Card,
  ContentCard,
  SkeletonLoader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Switch,
  Tooltip,
  MessageBar,
} from './ui';
// @ts-expect-error - SVG imports handled by vite-plugin-svgr
import { ReactComponent as DownloadMyDataIcon } from '../assets/img/Download-My-Data.svg';
// @ts-expect-error - SVG import
import { ReactComponent as ConsentIcon } from '../assets/img/Consent.svg';
// @ts-expect-error - SVG import
import { ReactComponent as EriseIcon } from '../assets/img/Erise.svg';
// @ts-expect-error - SVG import
import { ReactComponent as SellMyDataIcon } from '../assets/img/Sell-My-Data.svg';
// @ts-expect-error - SVG import
import { ReactComponent as UpdateIcon } from '../assets/img/Update.svg';

interface Preference {
  id: string;
  name: string;
  description: string;
  isEnable: boolean;
}

interface LocationState {
  message?: string;
}

const ACTION_CARDS = [
  {
    key: 'download',
    field: 'downloadEnable',
    type: REQUESTTYPES.DOWNLOAD,
    title: 'Download My Data',
    description: 'Download all data relating to you held by our organization',
    Icon: DownloadMyDataIcon,
  },
  {
    key: 'erase',
    field: 'eraseDataEnable',
    type: REQUESTTYPES.ERASE,
    title: 'Erase Me',
    description: 'Delete all data relating to you held by our organization',
    Icon: EriseIcon,
  },
  {
    key: 'consent',
    field: 'consentEnable',
    type: REQUESTTYPES.CONSENT,
    title: 'Consent',
    description: 'Manage your consent to our different data processing activities',
    Icon: ConsentIcon,
  },
  {
    key: 'donotsell',
    field: 'notSellMyDataEnable',
    type: REQUESTTYPES.DONOTSELL,
    title: 'Do Not Sell My Data',
    description: 'Do not sell my data to other organizations',
    Icon: SellMyDataIcon,
  },
  {
    key: 'inaccuracies',
    field: 'updateInaccuraciesEnable',
    type: REQUESTTYPES.INACCURACIES,
    title: 'Update Inaccuracies',
    description: "Update an inaccuracy you've noticed in our records",
    Icon: UpdateIcon,
  },
];

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const { headline, subtitle } = useOrgSettings();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<Preference[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [successMessageMain, setSuccessMessageMain] = useState<string | null>(null);

  const { data: tabConfigData, isLoading: isTabConfigLoading } = useTabConfiguration();
  const { data: privacyContentData, refetch: refetchPrivacyContent } = usePrivacyContent();
  const { data: dataPracticeData, refetch: refetchDataPractice } = useDataPractice();
  const { data: actionsData, refetch: refetchActions } = usePrivacyActions();
  const { data: preferencesData, isLoading: isPreferencesLoading, refetch: refetchPreferences } = usePreferences();

  useEffect(() => {
    if (state?.message) {
      setSuccessMessageMain(state.message);
      setTimeout(() => setSuccessMessageMain(null), 10000);
    }
  }, [state]);

  useEffect(() => {
    if (tabConfigData && tabConfigData.length > 0 && !activeTab) {
      const firstTab = tabConfigData[0].name;
      setActiveTab(firstTab);
      handleSelect(firstTab);
    }
  }, [tabConfigData, activeTab]);

  useEffect(() => {
    if (preferencesData) {
      const savedPreferences = cookie.load('preferences') as Preference[] | undefined;
      const merged = preferencesData.map((pref: any) => {
        const saved = savedPreferences?.find((s) => s.id === pref.id);
        return {
          ...pref,
          isEnable: saved ? saved.isEnable : pref.isEnable,
        };
      });
      setPreferences(merged);
    }
  }, [preferencesData]);

  const handleSelect = (key: string) => {
    setActiveTab(key);
    if (key === TABNAMES.ACTIONS) refetchActions();
    else if (key === TABNAMES.PREFERENCES) refetchPreferences();
    else if (key === TABNAMES.DATAPRACTICES) refetchDataPractice();
    else if (key === TABNAMES.POLICY) refetchPrivacyContent();
  };

  const redirectToDataSubjectRequest = (actionName: string) => {
    navigate('/datasubjectrequest', {
      state: { actionName },
      replace: true,
    });
  };

  const handleSwitchInputChange = (data: Preference, checked: boolean) => {
    setPreferences((prev) =>
      prev.map((item) => (item.id === data.id ? { ...item, isEnable: checked } : item))
    );
  };

  const savePreferences = () => {
    cookie.remove('preferences', { path: '/' });
    cookie.save('preferences', preferences, { path: '/' });
    setSuccessMessage('Preferences saved successfully.');
    setTimeout(() => setSuccessMessage(null), 10000);
  };

  const renderTabContent = (tabName: string) => {
    if (tabName === TABNAMES.PREFERENCES) {
      return <PreferencesTab
        preferences={preferences}
        isLoading={isPreferencesLoading}
        successMessage={successMessage}
        onToggle={handleSwitchInputChange}
        onSave={savePreferences}
      />;
    }

    if (tabName === TABNAMES.ACTIONS) {
      return <ActionsTab
        actionsData={actionsData}
        onAction={redirectToDataSubjectRequest}
      />;
    }

    if (tabName === TABNAMES.DATAPRACTICES) {
      return dataPracticeData ? (
        <ContentCard html={dataPracticeData.htmlContent || ''} className="mt-4" />
      ) : (
        <SkeletonLoader variant="text" count={5} className="mt-4" />
      );
    }

    if (tabName === TABNAMES.POLICY) {
      return privacyContentData ? (
        <ContentCard html={privacyContentData.htmlContent || ''} className="mt-4" />
      ) : (
        <SkeletonLoader variant="text" count={5} className="mt-4" />
      );
    }

    return null;
  };

  return (
    <div>
      {/* Success message */}
      {successMessageMain && (
        <div className="mb-6">
          <MessageBar variant="success">{successMessageMain}</MessageBar>
        </div>
      )}

      {/* Hero Section */}
      <Card className="border-t-4 border-primary bg-gradient-to-r from-primary/5 to-primary/10 px-6 py-5 mb-5">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          {headline || 'Privacy Center'}
        </h1>
        {subtitle && (
          <p className="text-gray-600 text-base leading-relaxed max-w-2xl">
            {subtitle}
          </p>
        )}
      </Card>

      {/* Tabs */}
      {isTabConfigLoading || !tabConfigData ? (
        <SkeletonLoader variant="text" count={3} />
      ) : (
        <Tabs
          value={activeTab || undefined}
          onValueChange={handleSelect}
        >
          <TabsList>
            {tabConfigData.map((t: any) => (
              <TabsTrigger key={t.id} value={t.name}>
                {t.displayName}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabConfigData.map((t: any) => (
            <TabsContent key={t.id} value={t.name}>
              <div className="mt-4">{renderTabContent(t.name)}</div>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};

/* --- Sub-components --- */

interface ActionsTabProps {
  actionsData: any;
  onAction: (actionName: string) => void;
}

function ActionsTab({ actionsData, onAction }: ActionsTabProps) {
  if (!actionsData) {
    return <SkeletonLoader variant="card" count={3} />;
  }

  const enabledActions = ACTION_CARDS.filter(
    (card) => (actionsData as any)[card.field]
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {enabledActions.map((card) => (
        <Card
          key={card.key}
          hover
          className="px-5 py-5 flex flex-col items-center text-center cursor-pointer"
        >
          <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center mb-3">
            <card.Icon className="w-5 h-5 text-primary" style={{ color: 'var(--color-primary)' }} />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">{card.title}</h3>
          <p className="text-sm text-gray-600 leading-snug mb-4 flex-1">
            {card.description}
          </p>
          <button
            onClick={() => onAction(card.type)}
            className="border border-primary text-primary hover:bg-primary hover:text-white rounded-lg px-4 py-1.5 text-sm font-medium transition-colors duration-200"
          >
            Proceed
          </button>
        </Card>
      ))}
    </div>
  );
}

interface PreferencesTabProps {
  preferences: Preference[];
  isLoading: boolean;
  successMessage: string | null;
  onToggle: (data: Preference, checked: boolean) => void;
  onSave: () => void;
}

function PreferencesTab({ preferences, isLoading, successMessage, onToggle, onSave }: PreferencesTabProps) {
  return (
    <Card className="p-6 sm:p-8">
      {successMessage && (
        <div className="mb-4">
          <MessageBar variant="success">{successMessage}</MessageBar>
        </div>
      )}

      <h2 className="text-xl font-bold text-gray-900 mb-2">You control your data.</h2>
      <p className="text-gray-600 leading-relaxed mb-6 max-w-xl">
        Our products are designed to protect your privacy. We won't collect data about you
        for advertising, analytics, or other non-essential things unless you give us
        permission to. You can change your preferences at any time.
      </p>

      {isLoading ? (
        <SkeletonLoader variant="text" count={4} />
      ) : preferences.length > 0 ? (
        <>
          <div className="divide-y divide-gray-100">
            {preferences.map((item) => (
              <div key={item.id} className="flex items-center justify-between py-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{item.name}</span>
                  <Tooltip content={item.description} side="right">
                    <svg className="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </Tooltip>
                </div>
                <Switch
                  onText="On"
                  offText="Off"
                  onCheckedChange={(checked) => onToggle(item, checked)}
                  defaultChecked={item.isEnable}
                />
              </div>
            ))}
          </div>

          <div className="mt-6">
            <button
              onClick={onSave}
              className="bg-primary text-white hover:bg-primary/90 rounded-lg px-6 py-2.5 text-sm font-medium transition-colors"
            >
              Save Preferences
            </button>
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-500">No preferences available.</p>
      )}
    </Card>
  );
}
